import { useState, useCallback } from 'react';
import { message } from 'antd';
import { pw9233Api, F9430, F9431 } from '../../../services/pw9233Api';
import { useFieldErrors } from '../../../hooks/useFieldErrors';
import { Mode } from './usePW9233Mode';

export function usePW9233Record() {
  const [record, setRecord] = useState<F9430 | null>(null);
  const [formData, setFormData] = useState<Partial<F9430>>({});
  const [details, setDetails] = useState<Partial<F9431>[]>([]);
  const [loading, setLoading] = useState(false);
  const { fieldErrors, setFieldErrors, clearFieldErrors, getFieldError, hasErrors } = useFieldErrors();

  /** Load a single record by composite key */
  const loadRecord = useCallback(async (empCode: string, yearMonth: string): Promise<F9430 | null> => {
    setLoading(true);
    try {
      const data = await pw9233Api.get(empCode, yearMonth);
      setRecord(data);
      setFormData({ ...data });
      setDetails(data.details || []);
      return data;
    } catch {
      message.error('載入失敗');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /** Set a single master field */
  const setField = useCallback(<K extends keyof F9430>(key: K, value: F9430[K]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  /** Get a single master field value */
  const getField = useCallback(<K extends keyof F9430>(key: K): F9430[K] | string => {
    return (formData as Partial<F9430>)[key] ?? '';
  }, [formData]);

  /** Update a detail row field */
  const updateDetailField = useCallback((rowIdx: number, field: keyof F9431, value: F9431[keyof F9431]) => {
    setDetails(prev => {
      const next = [...prev];
      if (next[rowIdx]) {
        next[rowIdx] = { ...next[rowIdx], [field]: value };
      }
      return next;
    });
  }, []);

  /** Save (create or update) */
  const handleSave = useCallback(async (
    mode: Mode,
    onSuccess: (result: F9430) => void,
  ) => {
    setLoading(true);
    try {
      const req = {
        master: formData as F9430,
        details: details as F9431[],
      };
      if (mode === 'add') {
        const result = await pw9233Api.create(req);
        message.success(`新增成功: ${result.f9430_01} / ${result.f9430_02}`);
        onSuccess(result);
      } else {
        const result = await pw9233Api.update(formData.f9430_01!, formData.f9430_02!, req);
        message.success('修改成功');
        setRecord(result);
        setFormData({ ...result });
        setDetails(result.details || []);
        onSuccess(result);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { errors?: { field: string; message: string }[]; error?: string } } };
      const errors = err.response?.data?.errors;
      if (errors && Array.isArray(errors)) {
        setFieldErrors(errors);
        message.error('請修正標示的欄位');
      } else {
        message.error(err.response?.data?.error || '儲存失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [formData, details, setFieldErrors]);

  /** Delete the current record */
  const handleDelete = useCallback(async (): Promise<boolean> => {
    if (!record) return false;
    try {
      await pw9233Api.delete(record.f9430_01, record.f9430_02);
      message.success('刪除成功');
      return true;
    } catch {
      message.error('刪除失敗');
      return false;
    }
  }, [record]);

  /** Add a new detail row */
  const addDetailRow = useCallback(() => {
    setDetails(prev => [
      ...prev,
      {
        f9431_01: formData.f9430_01 || '',
        f9431_02: formData.f9430_02 || '',
        f9431_03: prev.length + 1,
        f9431_11: '',
        f9431_11a: '',
        f9431_21: 0,
        f9431_22: 0,
        f9431_23: '',
      },
    ]);
  }, [formData.f9430_01, formData.f9430_02]);

  /** Delete a detail row by index */
  const deleteDetailRow = useCallback((rowIdx: number) => {
    setDetails(prev => prev.filter((_, i) => i !== rowIdx));
  }, []);

  /** Reset form to current record (revert) */
  const revertToRecord = useCallback(() => {
    clearFieldErrors();
    if (record) {
      setFormData({ ...record });
      setDetails(record.details || []);
    }
  }, [record, clearFieldErrors]);

  /** Reset form for a new add */
  const resetForAdd = useCallback(() => {
    clearFieldErrors();
    setRecord(null);
    setFormData({});
    setDetails([]);
  }, [clearFieldErrors]);

  /** Apply result after save */
  const applyResult = useCallback((result: F9430) => {
    setRecord(result);
    setFormData({ ...result });
    setDetails(result.details || []);
  }, []);

  return {
    record, formData, details, loading,
    setDetails,
    loadRecord, setField, getField,
    updateDetailField,
    handleSave, handleDelete,
    addDetailRow, deleteDetailRow,
    revertToRecord, resetForAdd, applyResult,
    fieldErrors, getFieldError, clearFieldErrors, hasErrors,
  };
}
